import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, Info, Plane, Clock } from 'lucide-react';
import Layout from '@/components/Layout';
import { springPresets } from '@/lib/motion';

const SECTIONS = [
  {
    id: 'baggage',
    title: '行李规定',
    icon: '🧳',
    items: [
      { q: '经济舱行李额度', a: '🧳🧳 托运 2件 × 每件 23kg（每件 L+W+H ≤ 158cm）+ 💼 手提 7kg（L+W+H ≤ 115cm）' },
      { q: '公务舱行李额度', a: '🧳🧳🧳 托运 3件 × 每件 23kg（每件 L+W+H ≤ 158cm）+ 💼 手提 10kg（L+W+H ≤ 115cm）' },
      { q: '超重行李收费', a: '超出额度部分每公斤约 USD 10–15，建议提前联系代表处确认' },
      { q: '婴儿行李', a: '2岁以下婴儿额外允许折叠婴儿车1件（不占行李额度）' },
      { q: '液体规定', a: '手提行李中液体须为 ≤100ml/件，并装入容量不超过1L的透明密封袋' },
      { q: '锂电池规定', a: '充电宝须随身携带（禁止托运），≤100Wh免申报，100–160Wh须申报' },
    ],
  },
  {
    id: 'visa',
    title: '签证与证件',
    icon: '🛂',
    items: [
      { q: '坦桑尼亚签证', a: '中国护照持有者可在坦桑尼亚口岸申请落地签证（点签），费用约USD 50，须携带彩色照片及美元现金' },
      { q: '桑给巴尔落地签', a: '中国公民前往桑给巴尔须办理落地签证，可在桑给巴尔机场抵达后申请，费用约USD 50，须携带彩色照片及美元现金，不可免签直接入境' },
      { q: '护照有效期', a: '入境坦桑尼亚要求护照在到达日起有效期不少于6个月，并有2页以上空白签证页' },
      { q: '黄热病接种', a: '过境或来自黄热病疫区国家的旅客需提供黄热病接种证明（小黄本）' },
      { q: '中国入境要求', a: '无需疫苗证明，但建议提前了解最新入境政策' },
    ],
  },
  {
    id: 'checkin',
    title: '值机与登机',
    icon: '✈️',
    items: [
      { q: '广州出发建议提前到达', a: '国际航班建议提前3小时到达广州白云机场T2航站楼，柜台一般在出发前2–3小时开放' },
      { q: '网上值机', a: '可通过坦桑尼亚航空官网或手机App提前48小时至1小时内完成在线值机和选座' },
      { q: '值机截止时间', a: '广州出发国际航班：起飞前1小时截止值机；登机门：起飞前30分钟关闭' },
      { q: '公务舱值机', a: '公务舱旅客可使用专属快速通道，享有优先值机和安检通道' },
      { q: '手机登机牌', a: '支持电子登机牌（手机截图），建议同时打印纸质备用' },
    ],
  },
  {
    id: 'transit',
    title: '中转指南',
    icon: '🔄',
    items: [
      { q: '经达累斯萨拉姆（DAR）中转', a: '须出关、提取行李，通过坦桑尼亚海关后重新办理登机手续。建议预留至少3小时中转时间' },
      { q: '行李直挂（非中转DAR）', a: '经第三国中转时，若24小时内衔接坦航旗下航班，行李可直挂至最终目的地' },
      { q: '第三国过境免签', a: '中国护照持有者经达累斯萨拉姆（DAR）机场过境，24小时内无需过境签' },
      { q: '达累斯萨拉姆候机设施', a: 'DAR机场提供免费Wi-Fi、餐厅、免税店；坦航金卡及公务舱旅客可使用贵宾候机室' },
    ],
  },
  {
    id: 'health',
    title: '健康与安全',
    icon: '🏥',
    items: [
      { q: '疟疾预防', a: '坦桑尼亚为疟疾高发区，建议出发前4周开始服用抗疟药（请遵医嘱），在目的地使用驱虫剂，避免傍晚被蚊虫叮咬' },
      { q: '饮用水', a: '建议只饮用瓶装水或煮沸的水，不喝自来水及冰块' },
      { q: '医疗准备', a: '建议购买含医疗和紧急救援覆盖的旅游保险，随身携带常用药物和处方' },
      { q: '高海拔提示', a: '攀登乞力马扎罗（5895m）需要充分体能准备，建议咨询医生。急性高原反应（AMS）风险较高' },
      { q: '食品安全', a: '避免食用未煮熟的食物及街头生食，推荐到正规餐厅用餐' },
    ],
  },
];

export default function TravelGuide() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-96 overflow-hidden">
        <img src="/images/fun-photo-hero.jpg" alt="出行指南" className="w-full h-full object-cover object-[center_top]" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-secondary/85" />
        <div className="absolute inset-0 flex items-center pt-20">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">出行指南</h1>
            <p className="text-white/70">行李 · 签证 · 值机 · 中转 · 健康与安全</p>
          </div>
        </div>
      </section>

      {/* Tips Banner */}
      <div className="bg-accent/10 border-b border-accent/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <AlertTriangle size={18} className="text-accent shrink-0" />
          <p className="text-sm">
            <span className="font-semibold">出行提示：</span>
            坦桑尼亚为东非主要旅游目的地。中国公民入境坦桑尼亚本土及桑给巴尔均须办理落地签证（VOA），费用约USD 50，须携带照片及美元现金。出发前请确保护照有效期≥6个月。
          </p>
        </div>
      </div>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Quick Nav */}
          <div className="flex flex-wrap gap-2 mb-10">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full text-sm hover:bg-primary/10 hover:text-primary transition-colors"
              >
                <span>{s.icon}</span>
                {s.title}
              </a>
            ))}
          </div>

          <div className="space-y-12">
            {SECTIONS.map((section, si) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springPresets.gentle, delay: si * 0.05 }}
                className="scroll-mt-24"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl">{section.icon}</span>
                  <h2 className="text-xl font-bold">{section.title}</h2>
                </div>
                <div className="space-y-3">
                  {section.items.map((item, i) => (
                    <div
                      key={i}
                      className="bg-card border border-border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-start"
                    >
                      <div className="flex items-start gap-2 md:col-span-1">
                        <Info size={15} className="text-primary mt-0.5 shrink-0" />
                        <span className="font-medium text-sm">{item.q}</span>
                      </div>
                      <div className="md:col-span-3 flex items-start gap-2">
                        <CheckCircle size={15} className="text-green-500 mt-0.5 shrink-0" />
                        <span className="text-muted-foreground text-sm leading-relaxed">{item.a}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 bg-secondary text-white rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Plane size={18} className="text-accent" />
                <span className="text-accent font-semibold text-sm">更多问题？</span>
              </div>
              <h3 className="text-xl font-bold mb-1">联系我们的中国代表处</h3>
              <p className="text-white/60 text-sm flex items-center gap-2">
                <Clock size={14} />
                周一至周五 09:00–18:00（北京时间）
              </p>
            </div>
            <a
              href="tel:020-38550084"
              className="px-6 py-3 bg-accent text-accent-foreground rounded-xl font-semibold whitespace-nowrap hover:bg-accent/90 transition-colors"
            >
              📞 020-38550084
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
